/**
 * The page a share link opens.
 *
 * A recipient reads the report here and nowhere else: there is no viewer, no
 * data source and no DICOMweb access behind this route, only the report body
 * and the slice snapshots the doctor attached to it. Every slice the report
 * mentions is openable full window — from its chip in the text or from the
 * figures underneath.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { renderReportContent } from '@ohif/ui-next';

import { CalendarDays, Clock, Link2Off, LoaderCircle, Printer, ShieldCheck } from 'lucide-react';

import SliceLightbox from './SliceLightbox';
import { fetchSharedReport, viewableSlices, type SharedReport as Report } from './sharedReportApi';

/**
 * The report sheet is a light document inside a dark app, so the theme
 * variables the shared renderer paints with are re-pointed for its subtree.
 * Overriding the variables rather than the markup keeps one renderer serving
 * both the doctor's panels and this page.
 */
const SHEET_THEME = {
  '--foreground': '216 12% 23%',
  '--muted-foreground': '226 12% 39%',
  '--secondary': '216 12% 91%',
  '--secondary-foreground': '216 12% 23%',
  '--primary': '205 83% 26%',
  '--highlight': '207 86% 29%',
  '--border': '216 12% 91%',
} as React.CSSProperties;

const DISPLAY_FONT = { fontFamily: 'Montserrat, Inter, system-ui, sans-serif' };

function formatDate(value?: string | null): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

export default function SharedReport() {
  const { t } = useTranslation('Medicaprof');
  const { shareToken } = useParams<{ shareToken: string }>();

  const [report, setReport] = useState<Report | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!shareToken) {
      setError(t('Error.InvalidLink'));
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    fetchSharedReport(shareToken)
      .then(payload => {
        if (cancelled) {
          return;
        }
        setReport(payload.data);
        setExpiresAt(payload.share?.expiresAt || null);
      })
      .catch(e => {
        if (!cancelled) {
          setError(e?.message || t('Error.OpenReport'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shareToken, t]);

  const slices = useMemo(() => viewableSlices(report), [report]);

  const openByRefId = useCallback(
    (refId: string) => {
      const at = slices.findIndex(s => s.refId === refId);
      if (at >= 0) {
        setOpenIndex(at);
      }
    },
    [slices]
  );

  const body = useMemo(
    () => renderReportContent(report?.content || null, openByRefId),
    [report, openByRefId]
  );

  const pageClass =
    'flex min-h-screen flex-col bg-[#f1f2f4] text-[16px] leading-6 text-[#333840] [font-family:Roboto,Inter,system-ui,sans-serif] print:bg-white';

  const header = (showPrint: boolean) => (
    <header className="flex h-12 flex-none items-center gap-3 bg-[#00264c] pl-5 pr-3 text-white print:hidden">
      <img
        src="/medicaprof-logo.svg"
        alt="medicaprof"
        className="h-7 w-auto brightness-0 invert"
      />
      <span className="flex-1" />
      {showPrint && (
        <button
          type="button"
          onClick={() => window.print()}
          className="flex h-9 items-center gap-2 rounded-lg border border-white/50 bg-transparent px-3.5 text-[14px] font-medium text-white transition-colors hover:bg-white/[.12] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#a8d1ef]"
        >
          <Printer size={18} />
          {t('Shared.Print')}
        </button>
      )}
    </header>
  );

  if (loading) {
    return (
      <div className={pageClass}>
        {header(false)}
        <div
          role="status"
          className="flex flex-1 flex-col items-center justify-center gap-3 text-[#585e70]"
        >
          <LoaderCircle
            size={28}
            className="animate-spin text-[#1e78b8]"
          />
          <span className="text-[15px] font-medium leading-5">{t('Link.Opening')}</span>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className={pageClass}>
        {header(false)}
        <div className="grid flex-1 place-items-center p-6">
          <div className="flex w-full max-w-[440px] flex-col items-center gap-3 rounded-lg border border-[#e3e5e8] bg-white p-8 text-center">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-[#fdf0f0] text-[#b01a1a]">
              <Link2Off size={26} />
            </span>
            <h1
              className="m-0 text-[24px] font-bold leading-8 text-[#252930]"
              style={DISPLAY_FONT}
            >
              {t('Link.Unavailable')}
            </h1>
            <p className="m-0 text-[15px] leading-[22px] text-[#585e70]">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const status = report.signed
    ? report.signedBy?.name
      ? t('Report.SavedBy', { name: report.signedBy.name })
      : t('Report.Saved')
    : t('Report.DraftShort');
  const signedOn = formatDate(report.signedAt || report.updatedAt || report.createdAt);

  return (
    <div
      className={pageClass}
      style={{ touchAction: 'pan-y' }}
    >
      {header(true)}

      <main className="w-full flex-1 px-6 pb-16 pt-10 print:p-0">
        <article
          className="mx-auto flex max-w-[54rem] flex-col gap-6 rounded-lg border border-[#e3e5e8] bg-white px-14 py-12 print:max-w-none print:rounded-none print:border-0 print:p-0 max-sm:px-6 max-sm:py-8"
          style={SHEET_THEME}
        >
          <header className="flex flex-col gap-3">
            <div className="hidden items-center justify-between border-b-2 border-[#00264c] pb-3 print:flex">
              <img
                src="/medicaprof-logo.svg"
                alt="medicaprof"
                className="h-6 w-auto"
              />
            </div>
            <h1
              className="m-0 text-[32px] font-bold leading-10 text-[#252930] [text-wrap:balance]"
              style={DISPLAY_FONT}
            >
              {report.title || t('Report.DefaultTitle')}
            </h1>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-[14px] leading-5 text-[#585e70]">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={16} />
                {status}
              </span>
              {signedOn && (
                <span className="flex items-center gap-1.5">
                  <CalendarDays size={16} />
                  {signedOn}
                </span>
              )}
              {expiresAt && (
                <span className="flex items-center gap-1.5">
                  <Clock size={16} />
                  {t('Report.LinkExpires', { date: formatDate(expiresAt) })}
                </span>
              )}
            </div>
          </header>

          <section className="medicaprof-shared-report text-[16px] leading-6 text-[#333840]">
            {body || <div className="text-[#585e70]">{t('Report.NoContent')}</div>}
          </section>

          {slices.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2
                className="m-0 text-[18px] font-bold leading-6 text-[#252930]"
                style={DISPLAY_FONT}
              >
                {t('Shared.ReferencedImages', { n: slices.length })}
              </h2>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
                {slices.map((slice, i) => {
                  const label = slice.label || t('Report.SliceLabel', { index: slice.sliceIndex });
                  return (
                    <figure
                      key={slice.refId}
                      className="m-0 flex break-inside-avoid flex-col gap-1.5"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenIndex(i)}
                        title={t('Shared.OpenImage', { label })}
                        aria-label={t('Shared.OpenImage', { label })}
                        className="relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden rounded border-0 bg-black p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2d8dd0]"
                      >
                        <img
                          src={slice.snapshotUrl as string}
                          alt={label}
                          className="h-full w-full object-contain"
                        />
                      </button>
                      <figcaption className="text-[13px] leading-[18px] text-[#585e70]">
                        {label}
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </section>
          )}

          <footer className="flex items-center gap-2 border-t border-[#e3e5e8] pt-4 text-[12px] leading-4 text-[#585e70]">
            <ShieldCheck
              size={16}
              className="flex-none"
            />
            {t('Shared.Confidential')}
          </footer>
        </article>
      </main>

      {openIndex !== null && (
        <SliceLightbox
          slices={slices}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </div>
  );
}
